<template>
  <div class="profil-security">
    <!-- ────────── Password change ────────── -->
    <div class="vx-row">
      <div class="vx-col w-full md:w-2/3">
        <div class="flex items-center mb-3">
          <feather-icon icon="LockIcon" svgClasses="w-5 h-5 mr-2" />
          <h5 class="m-0">Change password</h5>
        </div>
        <p class="text-sm mb-4 text-grey-darker">
          Use at least 12 characters. We recommend a passphrase or a password manager.
          We do not require periodic changes — only change your password when you suspect it has been exposed.
        </p>
        <vs-input
          type="password"
          v-model="currentPassword"
          label-placeholder="Current password"
          icon-pack="feather" icon="icon-lock" icon-no-border
          class="w-full"
        />
        <vs-input
          type="password"
          v-model="newPassword"
          label-placeholder="New password"
          icon-pack="feather" icon="icon-shield" icon-no-border
          class="w-full mt-6"
          :maxlength="64"
        />
        <vs-input
          type="password"
          v-model="confirmPassword"
          label-placeholder="Confirm new password"
          icon-pack="feather" icon="icon-shield" icon-no-border
          class="w-full mt-6"
          :maxlength="64"
        />

        <!-- Live strength indicator + requirements checklist -->
        <div class="mt-3" v-if="newPassword.length > 0">
          <div class="strength-bar"><div :class="['strength-fill', strengthClass]" :style="{width: ((zxScore + 1) * 20) + '%'}"></div></div>
          <p class="text-xs mt-1" :class="strengthTextClass">{{ strengthLabel }}</p>
          <p class="text-xs mt-1" v-if="zxFeedback">{{ zxFeedback }}</p>

          <ul class="pwd-checklist text-xs mt-2">
            <li :class="reqCurrent ? 'ok' : 'ko'">
              <span class="mark">{{ reqCurrent ? '✓' : '○' }}</span> Current password entered
            </li>
            <li :class="reqLength ? 'ok' : 'ko'">
              <span class="mark">{{ reqLength ? '✓' : '○' }}</span> At least 12 characters ({{ newPassword.length }}/12)
            </li>
            <li :class="reqStrength ? 'ok' : 'ko'">
              <span class="mark">{{ reqStrength ? '✓' : '○' }}</span> Strength: Strong or better (current: {{ strengthLabel }})
            </li>
            <li :class="reqMatch ? 'ok' : 'ko'">
              <span class="mark">{{ reqMatch ? '✓' : '○' }}</span> New password and confirmation match
            </li>
          </ul>
        </div>

        <vs-alert v-if="passwordError" color="danger" class="mt-3">{{ passwordError }}</vs-alert>
        <vs-alert v-if="passwordSuccess" color="success" class="mt-3">Password updated. Other devices will need to sign in again.</vs-alert>

        <div class="mt-4 flex justify-end">
          <vs-button color="primary" :disabled="!canSubmitPassword" @click="changePassword">Update password</vs-button>
        </div>
      </div>
    </div>

    <vs-divider class="my-8" />

    <!-- ────────── 2FA ────────── -->
    <div class="vx-row">
      <div class="vx-col w-full md:w-2/3">
        <div class="flex items-center mb-3">
          <feather-icon icon="ShieldIcon" svgClasses="w-5 h-5 mr-2" />
          <h5 class="m-0">Two-factor authentication</h5>
        </div>
        <p class="text-sm mb-4 text-grey-darker">
          Two-factor authentication is required on every XYLO account. Choose between an
          authenticator app (recommended) or email codes. Trusted devices skip the challenge
          for 24 hours.
        </p>

        <div class="mb-4">
          <vs-chip v-if="status && status.twofa_enabled" color="success">
            <feather-icon icon="ShieldIcon" svgClasses="w-4 h-4" class="mr-1" />
            Enabled — {{ status.twofa_method === 'totp' ? 'Authenticator app' : 'Email code' }}
          </vs-chip>
          <vs-chip v-else color="danger">
            <feather-icon icon="ShieldOffIcon" svgClasses="w-4 h-4" class="mr-1" />
            Disabled
          </vs-chip>
        </div>

        <div v-if="status && status.twofa_enabled">
          <vs-button color="warning" type="border" class="mr-2" @click="openRegenerate">Regenerate recovery codes</vs-button>
          <vs-button color="danger"  type="border" @click="openDisable">Disable 2FA</vs-button>
        </div>
        <div v-else>
          <vs-button color="success" @click="openEnable">Enable 2FA</vs-button>
        </div>
      </div>
    </div>

    <!-- ────────── Enable popup (reuses MandatoryTwoFAModal flow) ────────── -->
    <MandatoryTwoFAModal v-if="showEnableModal" :skip-prompt="true" />

    <!-- ────────── Disable popup ────────── -->
    <vs-popup title="Disable 2FA" :active.sync="showDisableModal" :button-close-hidden="false">
      <p class="text-sm mb-3">
        Disabling 2FA leaves your account protected only by your password. To confirm, enter
        your current password and a current 2FA code.
      </p>
      <vs-input type="password" v-model="disable.currentPassword" label-placeholder="Current password" class="w-full mt-4" />
      <vs-input v-model="disable.code" :label-placeholder="disable.useRecovery ? 'Recovery code' : '6-digit code'" class="w-full mt-4" />
      <div class="mt-3" v-if="isEmailMethod && !disable.useRecovery">
        <vs-button size="small" type="border" :disabled="disable.sending" @click="sendEmailChallenge('disable')">
          {{ disable.sentTo ? 'Resend email code' : 'Send code by email' }}
        </vs-button>
        <p v-if="disable.sentTo" class="text-xs mt-2 text-success">Code sent to {{ disable.sentTo }}. Check your inbox (10 min validity).</p>
      </div>
      <div class="mt-3">
        <vs-checkbox v-model="disable.useRecovery">Use a recovery code instead</vs-checkbox>
      </div>
      <vs-alert v-if="disable.error" color="danger" class="mt-3">{{ disable.error }}</vs-alert>
      <div class="flex justify-end mt-4">
        <vs-button type="border" class="mr-2" @click="showDisableModal = false">Cancel</vs-button>
        <vs-button color="danger" :disabled="!canSubmitDisable" @click="submitDisable">Disable</vs-button>
      </div>
    </vs-popup>

    <!-- ────────── Regenerate recovery codes popup ────────── -->
    <vs-popup title="Regenerate recovery codes" :active.sync="showRegenerateModal">
      <div v-if="!regen.recoveryCodes">
        <p class="text-sm mb-3">
          Enter a current 2FA code to confirm. The previous recovery codes will become invalid.
        </p>
        <vs-input v-model="regen.code" :label-placeholder="regen.useRecovery ? 'Recovery code' : '6-digit code'" class="w-full" />
        <div class="mt-3" v-if="isEmailMethod && !regen.useRecovery">
          <vs-button size="small" type="border" :disabled="regen.sending" @click="sendEmailChallenge('regen')">
            {{ regen.sentTo ? 'Resend email code' : 'Send code by email' }}
          </vs-button>
          <p v-if="regen.sentTo" class="text-xs mt-2 text-success">Code sent to {{ regen.sentTo }}. Check your inbox (10 min validity).</p>
        </div>
        <div class="mt-3">
          <vs-checkbox v-model="regen.useRecovery">Use a recovery code instead</vs-checkbox>
        </div>
        <vs-alert v-if="regen.error" color="danger" class="mt-3">{{ regen.error }}</vs-alert>
        <div class="flex justify-end mt-4">
          <vs-button type="border" class="mr-2" @click="showRegenerateModal = false">Cancel</vs-button>
          <vs-button color="primary" :disabled="!regen.code" @click="submitRegenerate">Generate new codes</vs-button>
        </div>
      </div>
      <div v-else>
        <vs-alert color="warning" class="mb-3" :active="true">
          Save these codes now — we won't show them again.
        </vs-alert>
        <pre class="recovery-block">{{ regen.recoveryCodes.join('\n') }}</pre>
        <div class="flex justify-end mt-4">
          <vs-button color="success" @click="showRegenerateModal = false">Done</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import twofa from '@/http/requests/auth/twofa.js'
import zxcvbn from 'zxcvbn'
import MandatoryTwoFAModal from '@/components/MandatoryTwoFAModal.vue'

export default {
  name: 'ProfilSecurity',
  components: { MandatoryTwoFAModal },
  data () {
    return {
      // password
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      passwordError: null,
      passwordSuccess: false,
      // 2FA status
      status: null,
      // modals
      showEnableModal: false,
      showDisableModal: false,
      showRegenerateModal: false,
      // disable
      disable: { currentPassword: '', code: '', useRecovery: false, error: null, sending: false, sentTo: null },
      // regenerate
      regen: { code: '', useRecovery: false, error: null, recoveryCodes: null, sending: false, sentTo: null }
    }
  },
  computed: {
    zx () { return this.newPassword ? zxcvbn(this.newPassword, this.userInputs) : null },
    zxScore () { return this.zx ? this.zx.score : 0 },
    zxFeedback () {
      if (!this.zx) return null
      const fb = this.zx.feedback || {}
      return fb.warning || (fb.suggestions || []).join(' ')
    },
    strengthLabel () {
      return ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][this.zxScore]
    },
    strengthClass () {
      return ['s-vweak', 's-weak', 's-fair', 's-strong', 's-vstrong'][this.zxScore]
    },
    strengthTextClass () {
      return this.zxScore >= 3 ? 'text-success' : 'text-warning'
    },
    userInputs () {
      return [
        localStorage.getItem('login-email'),
        localStorage.getItem('login-username')
      ].filter(Boolean)
    },
    reqCurrent  () { return !!this.currentPassword },
    reqLength   () { return this.newPassword.length >= 12 },
    reqStrength () { return this.zxScore >= 3 },
    reqMatch    () { return this.newPassword.length > 0 && this.newPassword === this.confirmPassword },
    canSubmitPassword () {
      return this.reqCurrent && this.reqLength && this.reqStrength && this.reqMatch
    },
    canSubmitDisable () {
      return this.disable.currentPassword && this.disable.code
    },
    isEmailMethod () {
      return !!(this.status && this.status.twofa_enabled && this.status.twofa_method === 'email')
    }
  },
  methods: {
    fetchStatus () {
      twofa.status().then(res => { this.status = res.data }).catch(() => {})
    },
    changePassword () {
      this.passwordError = null
      this.passwordSuccess = false
      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = 'New password and confirmation don\'t match.'
        return
      }
      this.$vs.loading()
      twofa.changePassword({
        currentPassword: this.currentPassword,
        newPassword: this.newPassword
      })
        .then(() => {
          this.$vs.loading.close()
          this.passwordSuccess = true
          this.currentPassword = this.newPassword = this.confirmPassword = ''
          // Server invalidates trusted devices on password change — drop the
          // local cached token so the next login will challenge 2FA.
          localStorage.removeItem('xylo:trusted-device-token')
        })
        .catch(err => {
          this.$vs.loading.close()
          this.passwordError = (err.response && err.response.data && err.response.data.message) || 'Could not update password.'
        })
    },
    openEnable () {
      // Reuse the same activation flow as the post-login modal by setting the
      // must-enable flag temporarily, opening a transient modal instance.
      localStorage.setItem('xylo:must-enable-2fa', '1')
      this.showEnableModal = true
      this.$nextTick(() => {
        // Watch for activation: poll status every 1.5s while modal is open.
        this._poll = setInterval(() => {
          if (!localStorage.getItem('xylo:must-enable-2fa')) {
            clearInterval(this._poll)
            this.showEnableModal = false
            this.fetchStatus()
          }
        }, 1500)
      })
    },
    openDisable () {
      this.disable = { currentPassword: '', code: '', useRecovery: false, error: null, sending: false, sentTo: null }
      this.showDisableModal = true
    },
    openRegenerate () {
      this.regen = { code: '', useRecovery: false, error: null, recoveryCodes: null, sending: false, sentTo: null }
      this.showRegenerateModal = true
    },
    sendEmailChallenge (target) {
      const ctx = target === 'regen' ? this.regen : this.disable
      ctx.sending = true
      ctx.error = null
      twofa.sendEmailChallenge()
        .then(res => {
          ctx.sending = false
          ctx.sentTo = (res.data && res.data.sentTo) || true
        })
        .catch(err => {
          ctx.sending = false
          ctx.error = (err.response && err.response.data && err.response.data.message) || 'Could not send the code.'
        })
    },
    submitDisable () {
      this.disable.error = null
      this.$vs.loading()
      twofa.disable({
        currentPassword: this.disable.currentPassword,
        code: this.disable.code,
        useRecovery: this.disable.useRecovery
      })
        .then(() => {
          this.$vs.loading.close()
          this.showDisableModal = false
          this.fetchStatus()
          // Server wipes trusted devices server-side; clear the client copy too.
          localStorage.removeItem('xylo:trusted-device-token')
          this.$vs.notify({ title: '2FA disabled', text: 'Two-factor authentication is now off.', color: 'warning', position: 'bottom-right' })
        })
        .catch(err => {
          this.$vs.loading.close()
          this.disable.error = (err.response && err.response.data && err.response.data.message) || 'Could not disable 2FA.'
        })
    },
    submitRegenerate () {
      this.regen.error = null
      this.$vs.loading()
      twofa.regenerateRecoveryCodes({
        code: this.regen.code,
        useRecovery: this.regen.useRecovery
      })
        .then(res => {
          this.$vs.loading.close()
          this.regen.recoveryCodes = res.data.recoveryCodes
        })
        .catch(err => {
          this.$vs.loading.close()
          this.regen.error = (err.response && err.response.data && err.response.data.message) || 'Could not regenerate codes.'
        })
    }
  },
  beforeDestroy () { if (this._poll) clearInterval(this._poll) },
  created () { this.fetchStatus() }
}
</script>

<style scoped>
.strength-bar {
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
}
.strength-fill {
  height: 100%;
  transition: width 200ms, background-color 200ms;
}
.s-vweak   { background: #d33; }
.s-weak    { background: #e66; }
.s-fair    { background: #ea0; }
.s-strong  { background: #6c0; }
.s-vstrong { background: #2a8; }
.pwd-checklist {
  list-style: none;
  padding: 0;
  margin: 0;
}
.pwd-checklist li {
  display: flex;
  align-items: center;
  line-height: 1.6;
}
.pwd-checklist .mark {
  display: inline-block;
  width: 14px;
  text-align: center;
  margin-right: 6px;
  font-weight: bold;
}
.pwd-checklist li.ok  { color: #2a8; }
.pwd-checklist li.ko  { color: #888; }
.recovery-block {
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px 16px;
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  line-height: 1.6;
  letter-spacing: 0.5px;
  margin: 0;
  white-space: pre-wrap;
}
</style>
